import { useCallback, useEffect, useRef, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { useVoiceVisualizer } from "react-voice-visualizer";

import { axiosInstance } from "../lib/axios";
import { isMSquaredClub } from "../utils/isEnv";

const SUPPORTED_LANGUAGES = ["en"] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

type SpeechToTextState = "idle" | "recording" | "processing";

type TranscriptionResponse = {
  text: string;
};

const transcribeAudio = async (audioBlob: Blob): Promise<string> => {
  const arrayBuffer = await audioBlob.arrayBuffer();
  const base64 = btoa(
    new Uint8Array(arrayBuffer).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      "",
    ),
  );

  const response = await axiosInstance.post<TranscriptionResponse>(
    "/speech/transcribe",
    { audio: base64, language: SUPPORTED_LANGUAGES[0] },
  );
  return response.data.text;
};

const MAX_RECORDING_SECONDS = isMSquaredClub() ? 180 : 60;

export const useSpeechToText = (onTranscript: (text: string) => void) => {
  const [transcriptionState, setTranscriptionState] =
    useState<SpeechToTextState>("idle");
  const cancelledRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mutation = useMutation({
    mutationFn: transcribeAudio,
    onSuccess: (text) => {
      if (text) onTranscript(text);
      setTranscriptionState("idle");
    },
    onError: () => {
      setTranscriptionState("idle");
    },
  });

  const recorderControls = useVoiceVisualizer({
    shouldHandleBeforeUnload: false,
  });

  const { recordedBlob, isRecordingInProgress, stopRecording, clearCanvas } =
    recorderControls;

  useEffect(() => {
    if (!recordedBlob || cancelledRef.current) return;
    setTranscriptionState("processing");
    mutation.mutate(recordedBlob);
  }, [recordedBlob]);

  const start = useCallback(() => {
    cancelledRef.current = false;
    recorderControls.startRecording();
  }, [recorderControls]);

  useEffect(() => {
    if (!isRecordingInProgress) return;

    timerRef.current = setTimeout(() => {
      stopRecording();
    }, MAX_RECORDING_SECONDS * 1000);

    return () => clearTimer();
  }, [isRecordingInProgress]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    clearTimer();
    stopRecording();
  }, [clearTimer, stopRecording]);

  const cancel = useCallback(() => {
    clearTimer();
    cancelledRef.current = true;
    stopRecording();
    clearCanvas();
    setTranscriptionState("idle");
  }, [clearTimer, stopRecording, clearCanvas]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const state: SpeechToTextState = isRecordingInProgress
    ? "recording"
    : transcriptionState;

  return {
    state,
    start,
    stop,
    cancel,
    recorderControls,
    isError: mutation.isError,
  };
};
